import React, { useEffect } from "react";

/**
 * Sets <title>, meta tags and JSON-LD dynamically (CRA / SPA).
 * Pass title, description, image (cover), imageAlt and url for OpenGraph + Twitter + Pinterest compatible cards.
 */
export default function SEOHead({
  title,
  description,
  image,
  imageAlt,
  url,
  type = "article",
  publishedTime,
  modifiedTime,
  author = "SF Learning",
}) {
  useEffect(() => {
    const safeTitle = title || "SF Learning — Formations & Accompagnements";
    const safeDescription = description || "";
    const safeImageAlt = imageAlt || title || "Image SF Learning";

    if (safeTitle) document.title = safeTitle;

    setMeta("description", safeDescription);
    setMeta("og:title", safeTitle, true);
    setMeta("og:description", safeDescription, true);
    setMeta("og:image", image, true);
    setMeta("og:image:alt", safeImageAlt, true);
    setMeta("og:url", url, true);
    setMeta("og:type", type, true);

    if (publishedTime) setMeta("article:published_time", publishedTime, true);
    if (modifiedTime) setMeta("article:modified_time", modifiedTime, true);
    if (author) setMeta("article:author", author, true);

    // Pinterest uses OpenGraph data when Rich Pins are enabled/validated.
    // These tags also help X/Twitter and other social previews.
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", safeTitle);
    setMeta("twitter:description", safeDescription);
    setMeta("twitter:image", image);
    setMeta("twitter:image:alt", safeImageAlt);

    setJsonLd("sf-imageobject-jsonld", image ? {
      "@context": "https://schema.org",
      "@type": "ImageObject",
      "contentUrl": image,
      "url": image,
      "name": safeImageAlt,
      "caption": safeImageAlt,
      "description": safeDescription,
      "representativeOfPage": true,
      ...(url ? { "mainEntityOfPage": url } : {}),
    } : null);

    return () => {
      document.title = "SF Learning — Formations & Accompagnements";
      removeJsonLd("sf-imageobject-jsonld");
    };
  }, [title, description, image, imageAlt, url, type, publishedTime, modifiedTime, author]);

  return null;
}

function setMeta(name, content, isProperty = false) {
  if (!content) return;
  const attr = isProperty ? "property" : "name";
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setJsonLd(id, data) {
  removeJsonLd(id);
  if (!data) return;
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

function removeJsonLd(id) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
}
